import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  DollarSign,
  Sparkles,
  UserCircle2,
  X,
} from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useConfetti } from '@/components/ConfettiOverlay';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { listContacts } from '@/lib/contacts';
import { goBackOrTo } from '@/lib/nav';
import type { ContactRow } from '@/db/types';
import {
  type ActiveTask,
  completeTask,
  getActiveProperty,
  getTaskById,
} from '@/lib/tasks';

export default function CompleteTask() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const db = useSQLiteContext();

  const [task, setTask] = useState<ActiveTask | null>(null);
  const [cost, setCost] = useState('');
  const [vendor, setVendor] = useState('');
  const [contact, setContact] = useState<ContactRow | null>(null);
  const [notes, setNotes] = useState('');
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const { fire } = useConfetti();

  const load = useCallback(async () => {
    if (!id) return;
    const [t, prop] = await Promise.all([getTaskById(db, id), getActiveProperty(db)]);
    setTask(t);
    if (prop) setContacts(await listContacts(db, prop.id));
    // Vendor memory — pre-fill the vendor field from the most-recent
    // completion of the same rule_key on this property. Saves the user
    // from re-typing "ABC HVAC" every annual tune-up. Only pre-fills
    // if the user hasn't already typed something.
    if (t?.rule_key && prop) {
      const last = await db.getFirstAsync<{ vendor: string | null }>(
        `SELECT tc.vendor
           FROM task_completion tc
           JOIN task tk ON tk.id = tc.task_id
          WHERE tk.property_id = ?
            AND tk.rule_key = ?
            AND tc.vendor IS NOT NULL
            AND tc.vendor != ''
       ORDER BY tc.completed_at DESC
          LIMIT 1`,
        prop.id,
        t.rule_key
      );
      if (last?.vendor) {
        setVendor((prev) => (prev.trim() === '' ? last.vendor! : prev));
      }
    }
  }, [db, id]);

  useEffect(() => {
    load();
  }, [load]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load])
  );

  if (!task) return <View style={styles.container} />;

  const cents = parseCostToCents(cost);

  const submit = async (e?: { nativeEvent: { pageX: number; pageY: number } }) => {
    if (working) return;
    setWorking(true);
    if (e) fire(e.nativeEvent.pageX, e.nativeEvent.pageY, 16);
    await completeTask(db, task.id, {
      cost_cents: cents,
      vendor: vendor.trim() || contact?.name || null,
      contact_id: contact?.id ?? null,
      notes: notes.trim() || null,
    });
    setTimeout(() => {
      router.dismissAll?.();
      goBackOrTo(router, '/(tabs)');
    }, 480);
  };

  const submitSkip = () => submit();

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Mark done</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
        <View style={styles.heroWrap}>
          <View style={styles.heroBadge}>
            <Sparkles size={16} color={Palette.honeyDeep} strokeWidth={1.7} />
            <Text style={styles.heroBadgeText}>NICE WORK</Text>
          </View>
          <Text style={styles.heroTitle}>{task.title}</Text>
          <Text style={styles.heroSub}>
            Add a few details and they become part of your home record — useful
            at refinance, insurance claims, or resale.
          </Text>
        </View>

        <Section title="Cost" subtitle="What did this run you (parts + labor)?">
          <View style={styles.costRow}>
            <View style={styles.dollar}>
              <DollarSign size={16} color={Palette.inkSoft} strokeWidth={1.7} />
            </View>
            <TextInput
              value={cost}
              onChangeText={setCost}
              placeholder="0.00"
              placeholderTextColor={Palette.inkFaint}
              inputMode="decimal"
              style={styles.costInput}
            />
          </View>
        </Section>

        <Section title="Who did it" subtitle="Pick a saved contact or type a name.">
          <Pressable
            onPress={() => setContactsOpen(true)}
            style={({ pressed }) => [styles.contactRow, pressed && { opacity: 0.94 }]}>
            <View style={styles.avatar}>
              <UserCircle2 size={18} color={Palette.forest} strokeWidth={1.6} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.contactText, !contact && styles.contactPlaceholder]}>
                {contact?.name ?? 'Choose from your contacts'}
              </Text>
              {contact ? (
                <Text style={styles.contactMeta}>
                  {[contact.role, contact.company].filter(Boolean).join(' · ')}
                </Text>
              ) : null}
            </View>
            <ChevronDown size={16} color={Palette.inkFaint} strokeWidth={1.7} />
          </Pressable>

          <Text style={[styles.orLabel]}>OR TYPE A NAME</Text>
          <TextInput
            value={vendor}
            onChangeText={setVendor}
            placeholder="e.g. I did it · Mike at Acme HVAC"
            placeholderTextColor={Palette.inkFaint}
            style={styles.fieldInput}
          />
        </Section>

        <Section title="Notes" subtitle="What was replaced, what you noticed, what to remember next time.">
          <TextInput
            value={notes}
            onChangeText={setNotes}
            placeholder="Optional"
            placeholderTextColor={Palette.inkFaint}
            multiline
            style={[styles.fieldInput, { height: 100, textAlignVertical: 'top' }]}
          />
        </Section>

        <Pressable
          onPress={(e) => submit(e as never)}
          disabled={working}
          style={({ pressed }) => [
            styles.confirmBtn,
            working && { opacity: 0.6 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color="#3A2A0C" strokeWidth={2.4} />
          <Text style={styles.confirmText}>Confirm & save</Text>
        </Pressable>

        <Pressable
          onPress={submitSkip}
          style={({ pressed }) => [styles.skipBtn, pressed && { opacity: 0.8 }]}>
          <Text style={styles.skipText}>Skip — just mark it done</Text>
        </Pressable>
      </ScrollView>

      <Modal animationType="fade" transparent visible={contactsOpen} onRequestClose={() => setContactsOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setContactsOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHead}>
              <Text style={styles.modalTitle}>Pick a contact</Text>
              <Pressable onPress={() => setContactsOpen(false)} hitSlop={8}>
                <X size={18} color={Palette.inkSoft} strokeWidth={1.8} />
              </Pressable>
            </View>
            {contacts.length === 0 ? (
              <Text style={styles.modalEmpty}>
                You haven&apos;t saved any contacts yet. Add one in the Contacts screen.
              </Text>
            ) : (
              <ScrollView style={{ maxHeight: 320 }}>
                {contacts.map((c) => (
                  <Pressable
                    key={c.id}
                    onPress={() => {
                      setContact(c);
                      setVendor('');
                      setContactsOpen(false);
                    }}
                    style={({ pressed }) => [styles.modalRow, pressed && { backgroundColor: '#EEF3EE' }]}>
                    <Text style={styles.modalRowName}>{c.name}</Text>
                    {c.role ? <Text style={styles.modalRowMeta}>{c.role}</Text> : null}
                  </Pressable>
                ))}
              </ScrollView>
            )}
            <Pressable
              onPress={() => {
                setContact(null);
                setContactsOpen(false);
              }}
              style={({ pressed }) => [styles.modalRow, pressed && { backgroundColor: '#EEF3EE' }]}>
              <Text style={[styles.modalRowName, { color: Palette.inkFaint }]}>Clear selection</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

    </SafeAreaView>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {subtitle ? <Text style={styles.sectionSub}>{subtitle}</Text> : null}
      <View style={{ marginTop: 10 }}>{children}</View>
    </View>
  );
}

function parseCostToCents(raw: string): number | null {
  const s = raw.replace(/[^0-9.]/g, '');
  if (!s) return null;
  const num = parseFloat(s);
  if (isNaN(num)) return null;
  return Math.round(num * 100);
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  heroWrap: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    padding: Spacing.four,
    backgroundColor: Palette.honeySoft,
    borderRadius: Radius.xl,
  },
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  heroBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.honeyDeep,
    letterSpacing: 0.8,
  },
  heroTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 22,
    color: '#3A2A06',
    marginTop: 8,
    letterSpacing: -0.3,
  },
  heroSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: '#5A4012',
    marginTop: 6,
    lineHeight: 19,
  },
  section: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  sectionTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  sectionSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    marginTop: 2,
    lineHeight: 18,
  },
  costRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
  },
  dollar: {
    paddingHorizontal: 12,
  },
  costInput: {
    flex: 1,
    paddingVertical: 11,
    paddingRight: 12,
    fontFamily: Fonts.sansBold,
    fontSize: 16,
    color: Palette.ink,
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#DCE7E1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  contactPlaceholder: {
    fontFamily: Fonts.sansMedium,
    color: Palette.inkFaint,
  },
  contactMeta: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    marginTop: 1,
  },
  orLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.5,
    marginTop: 14,
    marginBottom: 4,
  },
  fieldInput: {
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  confirmBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Palette.honey,
    paddingVertical: 16,
    borderRadius: 18,
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
  },
  confirmText: {
    color: '#3A2A0C',
    fontFamily: Fonts.sansBold,
    fontSize: 15.5,
  },
  skipBtn: {
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 4,
  },
  skipText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.inkFaint,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand,
    borderRadius: Radius.xl,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 380,
  },
  modalHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  modalTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
  },
  modalEmpty: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    paddingVertical: 16,
    textAlign: 'center',
  },
  modalRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 10,
  },
  modalRowName: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  modalRowMeta: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    marginTop: 1,
  },
});
